/*
 * FAQ — Everything You Need to Know About The CPG Institute.
 *
 * Reached from the top-right avatar menu → "FAQ". Structured as
 * collapsible sections with per-question accordion rows and a
 * search box that filters across every question + answer. The
 * left rail exposes the section titles as jump links on wide
 * screens; on mobile they collapse into a scrollable chip bar
 * above the content column so nothing overflows.
 */
import React, { useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { FAQ_SECTIONS } from "../data/faqSections";
import { Search, ChevronDown, HelpCircle } from "lucide-react";

// Total question count for the header stat pill.
const TOTAL_Q = FAQ_SECTIONS.reduce((n, s) => n + s.questions.length, 0);


export default function FAQ() {
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Case-insensitive filter across question + answer text. Sections
  // with zero matches collapse away entirely so the page never shows
  // an empty heading.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_SECTIONS;
    return FAQ_SECTIONS.map((sec) => ({
      ...sec,
      questions: sec.questions.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
      ),
    })).filter((sec) => sec.questions.length > 0);
  }, [query]);

  return (
    <AppShell title="FAQ">
      <div className="space-y-8" data-testid="faq-page">
        <header className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold text-primary">
            <HelpCircle className="h-3 w-3" /> {TOTAL_Q} questions
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Everything you need to know about The CPG Institute — from what the
            program covers, to how it works, to what to expect from
            certification, funding, and retail placement.
          </p>

          <div className="relative max-w-xl mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              data-testid="faq-search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition"
            />
          </div>
        </header>

        {/* Section chip row — appears above the content on mobile and
            beside it on wider screens. Anchor links jump to each
            section without a full route change. */}
        {!query && (
          <div
            className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 dark-scrollbar"
            data-testid="faq-section-nav"
          >
            {FAQ_SECTIONS.map((sec) => (
              <a
                key={sec.slug}
                href={`#faq-${sec.slug}`}
                className="whitespace-nowrap shrink-0 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border border-border bg-card hover:bg-muted transition"
              >
                {sec.title}
              </a>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div
            data-testid="faq-empty"
            className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
          >
            No questions match "{query}". Try a shorter keyword — for example
            "packaging" or "certification".
          </div>
        ) : (
          <div className="space-y-10">
            {filtered.map((sec) => (
              <section
                key={sec.slug}
                id={`faq-${sec.slug}`}
                data-testid={`faq-section-${sec.slug}`}
                className="scroll-mt-32"
              >
                <h2 className="text-xs font-black uppercase tracking-[0.24em] text-primary mb-3">
                  {sec.title}
                </h2>
                <ul className="divide-y divide-border rounded-2xl border border-border bg-card overflow-hidden">
                  {sec.questions.map((it, i) => {
                    const key = `${sec.slug}-${i}`;
                    const open = openKeys.has(key);
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          aria-expanded={open}
                          data-testid={`faq-toggle-${sec.slug}-${i}`}
                          className="w-full flex items-start gap-3 px-4 sm:px-5 py-4 text-left hover:bg-muted/40 transition"
                        >
                          <span className="flex-1 font-semibold text-sm sm:text-base leading-snug">
                            {it.q}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 mt-0.5 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        {open && (
                          <div
                            data-testid={`faq-answer-${sec.slug}-${i}`}
                            className="px-4 sm:px-5 pb-5 -mt-1 text-sm leading-relaxed text-muted-foreground"
                          >
                            {it.a}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
